import React from 'react';
import { distinctUntilChanged, map, startWith, switchMap, type Observable } from 'rxjs';
import navigationHistory from 'store/navigation-history';
import location from 'services/router/location';
import useReadonlyObservable from 'enhancers/use-readonly-observable';
import Html from 'components/html';
import deepEqual from 'utils/deep-equal';
import type { RouteMetadata } from 'contracts';

import NavigationMenu from '../navigation-menu';
import { Signature } from '../signatures';
import type { ElementSignature } from '../contracts';

import { getContainerMetadata, type ContainerMetadata } from './utils';
import store from './container.store';
import { testId } from './container.settings';
import styles from './container.module.scss';

interface LayoutPageContainer {
  children: React.ReactNode;
}

const onContainerSettingsChange$: Observable<ContainerMetadata> = navigationHistory.onChange$.pipe(
  map(() => getContainerMetadata(location.currentTyped<RouteMetadata>())),
  switchMap((metadata) =>
    store.onChange$.pipe(
      startWith(metadata),
      map(({ size }) => ({ ...metadata, size }))
    )
  ),
  distinctUntilChanged(deepEqual)
);

const Container: React.FunctionComponent<LayoutPageContainer> & ElementSignature = (props) => {
  const { children } = props;
  const [{ withSidebar, size }] = useReadonlyObservable(
    onContainerSettingsChange$,
    getContainerMetadata(location.currentTyped<RouteMetadata>())
  );

  return (
    <Html.div
      testId={testId.page}
      className={[
        styles.container,
        'container-fluid',
        withSidebar && 'flex-row',
        withSidebar && styles.fixedHeight,
        styles[size],
        styles.spaceless,
      ]}
    >
      {withSidebar && <NavigationMenu />}
      <Html.div className={[withSidebar && styles.wrapper, 'flex-grow-1']}>{children}</Html.div>
    </Html.div>
  );
};

Container.signature = Signature.PAGE_CONTAINER;

export type { LayoutPageContainer };
export default React.memo(Container);
